"use client";

import { useEffect, useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { PulsarService } from "@/services/pulsar.service";
import { createMainnetCampaignCollection } from "@/lib/mainnet-adapters";
import { useAuth } from "./useAuth";
import type { MainnetCampaignCollection } from "@/types/mainnet-campaign";

export function useMainnetCampaigns(): MainnetCampaignCollection & {
  now: number;
  isLoading: boolean;
  isError: boolean;
} {
  const { isAuthenticated, hasApiToken } = useAuth();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);

  const overviewQuery = useQuery({
    queryKey: ["mainnet", "campaigns"],
    queryFn: () => PulsarService.getMainnetCampaigns(),
    enabled: isAuthenticated && hasApiToken,
    refetchInterval: 30_000,
  });

  const partnerIds = overviewQuery.data?.partners.map((partner) => partner.partnerId) ?? [];

  const detailQueries = useQueries({
    queries: partnerIds.map((partnerId) => ({
      queryKey: ["mainnet", "campaigns", partnerId],
      queryFn: () => PulsarService.getMainnetCampaignDetail(partnerId),
      enabled: isAuthenticated && hasApiToken && !!overviewQuery.data,
      refetchInterval: 30_000,
    })),
  });

  const details = detailQueries
    .map((query) => query.data)
    .filter((detail): detail is NonNullable<typeof detail> => Boolean(detail));

  const data = useMemo<MainnetCampaignCollection>(() => {
    if (!overviewQuery.data) {
      return {
        campaigns: [],
        lastUpdatedAt: new Date().toISOString(),
      };
    }

    return createMainnetCampaignCollection(overviewQuery.data, details);
  }, [details, overviewQuery.data]);

  const isLoading =
    overviewQuery.isLoading ||
    (partnerIds.length > 0 && detailQueries.some((query) => query.isLoading && !query.data));

  const isError =
    overviewQuery.isError ||
    detailQueries.some((query) => query.isError);

  return {
    ...data,
    now,
    isLoading,
    isError,
  };
}
